import { ProfileUsers } from '../api/enumerations';

/**
 * Role-based access to the UI (REAL-3099): which roles see each menu entry,
 * page tab, sub-navigation item, queue chip or action. Full access roles
 * (platform admin and bank admin) see everything through `canSee`, so an
 * empty list means admins only.
 *
 * The backend does not block most of these keys (shared endpoints), so hiding
 * them here is the only barrier. Keys that mirror a backend policy say so.
 */
export type AccessKey =
  // main menu
  | 'home'
  | 'archive'
  | 'archiveLegacy'
  | 'dashboard'
  | 'preflight'
  | 'finance'
  | 'userPanel'
  | 'notifications'
  | 'forms'
  | 'hive'
  // The Hive tabs
  | 'hiveRequests'
  | 'hiveLicensed'
  | 'hiveNotices'
  | 'hiveDashboard'
  // Dashboard credenciado sub-navigation
  | 'networkInAccreditation'
  | 'networkRecruitment'
  | 'networkCompliance'
  | 'networkOwnershipRelease'
  // notice tabs
  | 'noticeConfig'
  | 'noticeProducts'
  | 'noticeDistribution'
  | 'noticeLicensed'
  | 'noticeRequests'
  | 'noticeLimits'
  // accredited company profile tabs
  | 'noticeCompanyFinance'
  // accreditation request tabs
  | 'legalAnalysis'
  | 'engineeringAnalysis'
  // analysis queue chips
  | 'analysisQueueLegal'
  | 'analysisQueueEngineering'
  | 'analysisQueueContract'
  // actions
  | 'hivePullFromQueue'
  | 'hiveLiftQueueBan';

const {
  ARCHITECT,
  BANCO,
  BANK_APENAS_LEITURA,
  BANK_GESTOR_CREDENCIADO,
  BANK_GESTOR_EDITAIS,
  BANK_OPERADOR,
  BANK_RESPONSAVEL_TECNICO,
  COMERCIAL,
  CORPORATE,
  CORRETOR,
  ENGINEER,
  FINANCEIRO,
  JURIDICO,
  LANDING,
  CS,
} = ProfileUsers;

export const ACCESS_ROLES: Record<AccessKey, number[]> = {
  // --- Main menu -----------------------------------------------------------
  home: [BANK_RESPONSAVEL_TECNICO],
  // Read-only users (avaliahub-api's former "viewer") consult the archive too.
  archive: [BANK_RESPONSAVEL_TECNICO, BANK_APENAS_LEITURA],
  // Arquivados > Legado tab (B3 import): same audience as the archive itself.
  archiveLegacy: [BANK_RESPONSAVEL_TECNICO, BANK_APENAS_LEITURA],
  dashboard: [],
  preflight: [BANK_RESPONSAVEL_TECNICO],
  finance: [FINANCEIRO],
  userPanel: [],
  notifications: [BANK_RESPONSAVEL_TECNICO],
  forms: [BANK_RESPONSAVEL_TECNICO],
  // Bank managers and RealPrice engineers were added for the PROT-2950
  // screens (pair limits, waitlist control, analysis queues); the /the-hive
  // PrivateRoute allowedRoles list in App.tsx must stay in sync with `hive`,
  // or these grants become unreachable deep-links.
  hive: [
    BANK_RESPONSAVEL_TECNICO,
    FINANCEIRO,
    JURIDICO,
    CS,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],

  // --- The Hive tabs -------------------------------------------------------
  hiveRequests: [BANK_RESPONSAVEL_TECNICO],
  hiveLicensed: [BANK_RESPONSAVEL_TECNICO],
  hiveNotices: [
    BANK_RESPONSAVEL_TECNICO,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],
  hiveDashboard: [FINANCEIRO, CS],

  // --- Dashboard credenciado sub-navigation --------------------------------
  // Em Credenciamento and Preenchimento: admins only.
  networkInAccreditation: [],
  networkRecruitment: [],
  networkCompliance: [CS],
  networkOwnershipRelease: [CS],

  // --- Notice tabs ---------------------------------------------------------
  noticeConfig: [],
  noticeProducts: [],
  // bank_gestor_editais edits the queue rules too (hive-queue-rules-config),
  // but not the products: the tab hides its link to them for that role.
  noticeDistribution: [BANK_GESTOR_EDITAIS],
  noticeLicensed: [],
  noticeRequests: [
    BANK_RESPONSAVEL_TECNICO,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
    ENGINEER,
  ],
  // Pair limits + waitlist control (PROT-2950), mirroring the backend
  // policy: bank_admin/bank_gestor_editais on banks, engenheiro/admin on
  // RealPrice. BANK_RESPONSAVEL_TECNICO was removed — the backend refuses
  // both the GET and the PATCH for that role, so the tab was a dead end.
  noticeLimits: [BANK_GESTOR_EDITAIS, ENGINEER],

  // --- Accredited company profile tabs -------------------------------------
  // Payments of the company in the notice (REAL-3271).
  noticeCompanyFinance: [FINANCEIRO],

  // --- Accreditation request tabs (REAL-3102) ------------------------------
  legalAnalysis: [JURIDICO],
  engineeringAnalysis: [BANK_RESPONSAVEL_TECNICO],

  // --- Analysis queue chips ------------------------------------------------
  // Mirroring the backend policy (PROT-2950): bank managers see every queue
  // (the bank juridico chip only the legal one); RealPrice juridico -> legal,
  // engenheiro -> engineering, both -> contract; admin sees everything.
  analysisQueueLegal: [JURIDICO, BANK_GESTOR_EDITAIS, BANK_GESTOR_CREDENCIADO],
  analysisQueueEngineering: [
    ENGINEER,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
  ],
  analysisQueueContract: [
    ENGINEER,
    JURIDICO,
    BANK_GESTOR_EDITAIS,
    BANK_GESTOR_CREDENCIADO,
  ],

  // --- Actions -------------------------------------------------------------
  // Cancel a pending queue offer (REAL-3271), mirroring the API policy of
  // DELETE /hive-requests/{id}: on banks only bank_admin and bank_operador;
  // hive owners outside the bank roles are not role-gated.
  hivePullFromQueue: [
    BANK_OPERADOR,
    ARCHITECT,
    ENGINEER,
    CORPORATE,
    BANCO,
    LANDING,
    COMERCIAL,
    FINANCEIRO,
    CORRETOR,
    JURIDICO,
    CS,
  ],
  // Lift a company's queue acceptance ban, mirroring the API policy of
  // DELETE .../queue-ban (and of the notice PATCH): on banks only bank_admin
  // and bank_gestor_editais; hive owners outside the bank roles any role.
  hiveLiftQueueBan: [
    BANK_GESTOR_EDITAIS,
    ARCHITECT,
    ENGINEER,
    CORPORATE,
    BANCO,
    LANDING,
    COMERCIAL,
    FINANCEIRO,
    CORRETOR,
    JURIDICO,
    CS,
  ],
};

/** Roles that bypass the map: platform admin (3) and bank admin (10). */
export const isFullAccessRole = (role: number | null | undefined): boolean =>
  role === ProfileUsers.ADMIN || role === ProfileUsers.BANK_ADMIN;

/** Full access sees everything; otherwise the role must be in the key's list. */
export const canSee = (key: AccessKey, role: number | null): boolean => {
  if (role == null) return false;
  if (isFullAccessRole(role)) return true;
  return ACCESS_ROLES[key].includes(role);
};
